import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from "../contexts/AuthContext";
import { useSearchParams } from 'react-router-dom';
import { 
  Upload, Search, Grid, List, Plus, FileText, AlertCircle, Building, User, Filter,
  Eye, Download, Trash2, Clock, Tag, Sparkles, Brain, Zap, Calendar, FolderOpen, Pencil
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DocumentList from "@/components/documents/DocumentList";
import DocumentViewer from "@/components/documents/DocumentViewer";
import EvidenceViewerModal from "@/components/documents/EvidenceViewerModal";
import DocumentAnalysisModal from "@/components/documents/DocumentAnalysisModal";
import UniversalDocumentUpload from "@/components/UniversalDocumentUpload";
import DocumentAssignmentModal from "@/components/documents/DocumentAssignmentModal";
import ContractReviewModal from '@/components/contracts/ContractReviewModal';
import { IntentBridge } from '@/components/IntentBridge';
import documentsService from "@/services/documentsService";
import { propertiesService } from "@/services/propertiesService";
import { useToast } from '../components/notifications/ToastProvider';
import activityService from '../services/activityService';

const DocumentsPage = () => {
  const { user } = useAuth();
  // URL Parameters
  const [searchParams, setSearchParams] = useSearchParams();

  // State management (FIXED - removed problematic dependencies)
  const [documents, setDocuments] = useState([]);
  const { showSuccess, showError, showUploadSequence } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [viewMode, setViewMode] = useState("grid");
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ totalDocuments: 0, totalSize: 0, thisWeek: 0 });

  // Assignment states
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("all");
  const [selectedTenant, setSelectedTenant] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  // Vault v2: Pagination state
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [hasMoreDocuments, setHasMoreDocuments] = useState(false);
  const [documentOffset, setDocumentOffset] = useState(0);
  const DOCS_PER_PAGE = 50;

  // Upload assignment states
  const [uploadProperty, setUploadProperty] = useState("none");
  const [uploadTenant, setUploadTenant] = useState("none");
  const [uploadExpanded, setUploadExpanded] = useState(false);

  // Document viewer state
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  // Evidence-first viewer state: resolves ?artifact=doc_<uuid> via authenticated Clara proxy.
  const [evidenceArtifactReference, setEvidenceArtifactReference] = useState(null);
  const [isEvidenceViewerOpen, setIsEvidenceViewerOpen] = useState(false);

  // Contract review modal state
  const [showContractReview, setShowContractReview] = useState(false);
  const [detectedContractData, setDetectedContractData] = useState(null);
  const [contractDocument, setContractDocument] = useState(null);

  // Document analysis state
  const [analysisDocument, setAnalysisDocument] = useState(null);
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const [showUniversalUpload, setShowUniversalUpload] = useState(false);  const [uploadDocumentType, setUploadDocumentType] = useState("general");
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);

  // ===== DECISION LAYER v3: Persistenz-State =====
  const [resolvedDocs, setResolvedDocs] = React.useState(new Set());

  // IntentBridge state (for non-invasive contract detection)
  const [intentBridge, setIntentBridge] = useState({
    active: false,
    contractData: null,
    document: null
  });
  const [selectedDocumentForAssignment, setSelectedDocumentForAssignment] = useState(null);
  const [documentInsights, setDocumentInsights] = useState(new Map());

  // Upload states
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  // Process URL parameters
  useEffect(() => {
    const propertyId = searchParams.get('property');
    const tenantId = searchParams.get('tenant');
    const category = searchParams.get('category');
    const artifactReference = searchParams.get('artifact');
    
    if (propertyId) setSelectedProperty(propertyId);
    if (tenantId) setSelectedTenant(tenantId);
    if (category) setSelectedCategory(category);
    if (artifactReference && /^doc_[0-9a-f-]{36}$/i.test(artifactReference)) {
      setEvidenceArtifactReference(artifactReference);
      setIsEvidenceViewerOpen(true);
    }
  }, [searchParams]);

  // FIXED - Simplified data loading without problematic dependencies
  const loadDocuments = useCallback(async (offset = 0, append = false) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📄 Loading documents (offset=' + offset + ')...');
      const result = await documentsService.getDocuments(null, null, null, {
        limit: DOCS_PER_PAGE,
        offset
      });
      
      // Handle both old (array) and new (object with pagination) response formats
      const docs = Array.isArray(result) ? result : (result?.documents || []);
      const total = result?.total ?? docs.length;
      const hasMore = result?.hasMore ?? false;
      
      console.log('📄 Loaded:', docs.length, '/', total, 'hasMore:', hasMore);
      
      if (append) {
        // FIX: Use functional update to avoid 'documents' dependency
        setDocuments(prev => {
          const combined = [...prev, ...docs];
          // Update categories from combined list (functional, no external state read)
          const uniqueCategories = [...new Set(combined.map(doc => doc.category).filter(Boolean))];
          setCategories(uniqueCategories);
          return combined;
        });
      } else {
        setDocuments(docs);
    // v3: resolvedDocs aus Supabase initialisieren (Soft-Reopen: updated_at > resolved_at → nicht resolved)
    const resolvedSet = new Set(
      docs
        .filter(d => {
          if (!d.is_resolved) return false;
          // Soft-Reopen: wenn Dokument nach resolved_at aktualisiert wurde → wieder öffnen
          if (d.resolved_at && d.updated_at) {
            const rt = new Date(d.resolved_at).getTime();
            const ut = new Date(d.updated_at).getTime();
          }
          return true;
        })
        .map(d => d.id)
    );
    setResolvedDocs(resolvedSet);
        // Extract categories from fresh docs only
        const uniqueCategories = [...new Set(docs.map(doc => doc.category).filter(Boolean))];
        setCategories(uniqueCategories);
      }
      
      setTotalDocuments(total);
      setHasMoreDocuments(hasMore);
      setDocumentOffset(offset);
      
      console.log(`✅ Loaded ${docs.length} documents (total: ${total})`);
    } catch (error) {
      console.error('❌ Failed to load documents:', error);
      setError('Fehler beim Laden der Dokumente. Bitte versuchen Sie es erneut.');
      showError('Fehler beim Laden der Dokumente');
      if (!append) setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, showError]);

  const loadStats = useCallback(async () => {
    try {
      console.log('📊 Loading stats...');
      const statsData = await documentsService.getStats(null, null, user?.id);
      console.log('📊 Stats loaded:', statsData);
      setStats(statsData || { totalDocuments: 0, totalSize: 0, thisWeek: 0 });
    } catch (error) {
      console.error('❌ Failed to load stats:', error);
      setStats({ totalDocuments: 0, totalSize: 0, thisWeek: 0 });
    }
  }, []);

  const loadProperties = useCallback(async () => {
    try {
      console.log('🏢 Loading properties...');
      const propertiesData = await propertiesService.getProperties();
      console.log('🏢 Properties loaded:', propertiesData);
      setProperties(propertiesData || []);
    } catch (error) {
      console.error('❌ Failed to load properties:', error);
      setProperties([]);
    }
  }, []);

  const loadTenants = useCallback(async () => {
    try {
      console.log('👥 Loading tenants...');
      const tenantsData = await propertiesService.getTenants();
      console.log('👥 Tenants loaded:', tenantsData);
      setTenants(tenantsData || []);
    } catch (error) {
      console.error('❌ Failed to load tenants:', error);
      setTenants([]);
    }
  }, []);

  // Initial data loading
  useEffect(() => {
    console.log('🚀 Initial data loading started...');
    loadDocuments();
    try { loadStats(); } catch(e) { console.error("❌ loadStats crashed:", e); }
    loadProperties();
    loadTenants();
  }, [loadDocuments, loadStats, loadProperties, loadTenants]);

  // Upload functionality
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    // 🔍 DEBUG: Check user state
    console.log('🔍 DEBUG - Upload started');
    console.log('🔍 user:', user);
    console.log('🔍 user?.id:', user?.id);
    console.log('🔍 user?.email:', user?.email);

    setIsUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`📤 Uploading file ${i + 1}/${files.length}:`, file.name);

        setUploadProgress(((i + 0.5) / files.length) * 100);

        const metadata = {
          property_id: uploadProperty !== "none" ? uploadProperty : null,
          tenant_id: uploadTenant !== "none" ? uploadTenant : null,
          uploaded_by: user?.id, // ✅ FIX: Set uploaded_by so documents appear in list
        };
        
        // 🔍 DEBUG: Check metadata
        console.log('🔍 metadata:', metadata);

        const uploadResult = await documentsService.uploadDocument(file, metadata);
        setUploadProgress(((i + 1) / files.length) * 100);

        showUploadSequence(file.name);

        // Auto-trigger OCR analysis + Contract Detection for uploaded document
        if (uploadResult && uploadResult.id) {
          console.log(`🧠 Auto-triggering OCR + Contract Detection for: ${uploadResult.id}`);
          
          try {
            // 1. OCR Analysis
            const formData = new FormData();
            formData.append('document_id', uploadResult.id);
            formData.append('file_url', uploadResult.file_url);
            formData.append('filename', file.name);
            
            const ocrResponse = await fetch('/api/analyze-document', {
              method: 'POST',
              body: formData
            });
            
            if (ocrResponse.ok) {
              const ocrData = await ocrResponse.json();
              
              // 🔍 COMPREHENSIVE LOGGING FOR DEBUGGING
              console.log('=== OCR RESPONSE DEBUG ===');
              console.log('Full ocrData:', ocrData);
              console.log('extracted_text length:', ocrData.extracted_text?.length || 0);
              console.log('is_contract:', ocrData.is_contract);
              console.log('contract_data:', ocrData.contract_data);
              console.log('ocrResult:', ocrData.ocrResult);
              console.log('ocrResult.is_contract:', ocrData.ocrResult?.is_contract);
              console.log('ocrResult.contract_data:', ocrData.ocrResult?.contract_data);
              console.log('========================');
              
              // Check if backend detected a contract (check both levels)
              const isContract = ocrData.is_contract || ocrData.ocrResult?.is_contract;
              const contractData = ocrData.contract_data || ocrData.ocrResult?.contract_data;
              
              if (isContract && contractData) {
                console.log(`✅ Contract detected! Type: ${contractData.contract_type}, Provider: ${contractData.provider}`);
                
                // Store contract data and show IntentBridge (non-invasive notification)
                setDetectedContractData(contractData);
                setContractDocument(uploadResult);
                setIntentBridge({
                  active: true,
                  contractData: contractData,
                  document: uploadResult
                });
                
                showSuccess(`🎯 Vertrag erkannt: ${contractData.provider || 'Unbekannt'}`);
              } else {
                console.log(`ℹ️ No contract detected (is_contract: ${isContract}, has contract_data: ${!!contractData})`);
              }
            } else {
              console.error('❌ OCR Response not OK:', ocrResponse.status, ocrResponse.statusText);
            }
          } catch (analysisError) {
            console.error('❌ DEBUG: Analysis EXCEPTION:', analysisError);
            console.error('❌ DEBUG: Exception details:', analysisError.message, analysisError.stack);
            console.error('❌ Analysis error:', analysisError);
            // Don't show error to user - analysis is optional
          }
        }
      }

      // Reload documents to show new uploads
      await loadDocuments();
      try { await loadStats(); } catch(e) { console.error("❌ loadStats crashed:", e); }

      // Reset upload states
      setUploadProperty("none");
      setUploadTenant("none");

    } catch (error) {
      console.error('❌ Upload failed:', error);
      showError(`Fehler beim Hochladen: ${error.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // AI Analysis functionality
  const handleAnalyzeDocument = (document) => {
    setAnalysisDocument(document);
    setIsAnalysisOpen(true);
  };

    // Handle intelligent upload completion
  const handleIntelligentUploadComplete = async (result) => {
    console.log("🚀 Intelligent upload completed:", result);
    
    // Close upload modal
    setShowUniversalUpload(false);
    
    // Wait a moment for upload to complete
    setTimeout(() => {
      // Open DocumentAnalysisModal for analysis and processing
      setAnalysisDocument(result.document);
      setIsAnalysisOpen(true);
    }, 500);
  };

  const handleAnalysisComplete = (documentId, insights) => {
    setDocumentInsights(prev => new Map(prev.set(documentId, insights)));
    showSuccess('📊 Dokumentenanalyse abgeschlossen');
  };

  const getDocumentInsights = (documentId) => {
    return documentInsights.get(documentId);
  };

  // IntentBridge action handler
  const handleIntentAction = (action) => {
    console.log(`🎯 IntentBridge action: ${action}`);
    
    if (action === 'view') {
      // Open DocumentAnalysisModal to view contract details
      setAnalysisDocument(intentBridge.document);
      setIsAnalysisOpen(true);
      setIntentBridge({ active: false, contractData: null, document: null });
    }
    
    if (action === 'save') {
      // Open ContractReviewModal to create contract
      setShowContractReview(true);
      setIntentBridge({ active: false, contractData: null, document: null });
    }
    
    if (action === 'ignore') {
      // Close IntentBridge without action
      setIntentBridge({ active: false, contractData: null, document: null });
      console.log('🚫 Contract detection ignored by user');
    }
  };

  // Drag and drop functionality
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const handleInputChange = (e) => {
    const files = Array.from(e.target.files);
    handleFileUpload(files);
  };

  const handleDocumentClick = (document) => {
    setSelectedDocument(document);
    setIsViewerOpen(true);
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      const documentToDelete = documents.find(doc => doc.id === documentId);
      
      await documentsService.deleteDocument(documentId);
      await loadDocuments();
      try { await loadStats(); } catch(e) { console.error("❌ loadStats crashed:", e); }
      
      if (documentToDelete) {
        await activityService.logDocumentDeleted(documentToDelete);
        showSuccess(`Dokument "${documentToDelete.name}" wurde gelöscht`);
      }
    } catch (error) {
      console.error("Failed to delete document:", error);
      setError("Fehler beim Löschen des Dokuments");
      showError("Fehler beim Löschen des Dokuments");
    }
  };

  // FIXED - Safer filter logic with better debugging
  const filteredDocuments = React.useMemo(() => {
    console.log('🔍 Filtering documents...');
    console.log('🔍 Total documents:', documents.length);
    console.log('🔍 Search term:', searchTerm);
    console.log('🔍 Selected category:', selectedCategory);
    console.log('🔍 Selected property:', selectedProperty);
    console.log('🔍 Selected tenant:', selectedTenant);

    if (!Array.isArray(documents)) {
      console.warn('⚠️ Documents is not an array:', documents);
      return [];
    }

    const filtered = documents.filter(doc => {
      if (!doc) return false;

      const matchesSearch = !searchTerm || 
        (doc.name && doc.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (doc.description && doc.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCategory = selectedCategory === "all" || doc.category === selectedCategory || doc.document_type === selectedCategory;
      // STATUS: based on actual property/tenant assignment + AI confidence
      const isLinked = !!(doc.property_id || doc.tenant_id);
      const needsReview = !isLinked && doc.ai_confidence && doc.ai_confidence < 0.7;
      const isUnassigned = !isLinked && !needsReview;
      const matchesStatus = selectedStatus === "all" ||
        (selectedStatus === "completed" && isLinked) ||
        (selectedStatus === "needs_review" && (needsReview || (!isLinked && doc.processing_status === "needs_review"))) ||
        (selectedStatus === "unassigned" && isUnassigned);
      
      const matchesProperty = selectedProperty === "all" || 
        (selectedProperty === "none" && !doc.property_id) || 
        doc.property_id === selectedProperty;
      
      const matchesTenant = selectedTenant === "all" || 
        (selectedTenant === "none" && !doc.tenant_id) || 
        doc.tenant_id === selectedTenant;
      
      const matches = matchesSearch && matchesCategory && matchesProperty && matchesTenant && matchesStatus;
      
      if (!matches) {
        console.log('🔍 Document filtered out:', doc.name, {
          matchesSearch,
          matchesCategory,
          matchesProperty,
          matchesTenant
        });
      }
      
      return matches;
    });

    console.log('🔍 Filtered documents:', filtered.length);
    return filtered;
  }, [documents, searchTerm, selectedCategory, selectedProperty, selectedTenant]);

  // FIXED - Safer sort logic
  const sortedDocuments = React.useMemo(() => {
    console.log('📊 Sorting documents...');
    console.log('📊 Sort by:', sortBy);
    console.log('📊 Documents to sort:', filteredDocuments.length);

    if (!Array.isArray(filteredDocuments)) {
      console.warn('⚠️ FilteredDocuments is not an array:', filteredDocuments);
      return [];
    }

    const sorted = [...filteredDocuments].sort((a, b) => {
      if (!a || !b) return 0;

      switch (sortBy) {
        case "name":
          return (a.name || "").localeCompare(b.name || "");
        case "size":
          return (b.file_size || 0) - (a.file_size || 0);
        case "category":
          return (a.category || "").localeCompare(b.category || "");
        case "date":
        default:
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });

    console.log('📊 Sorted documents:', sorted.length);
    return sorted;
  }, [filteredDocuments, sortBy]);

  // ===== DECISION LAYER v2 =====
  const computePriority = React.useCallback((doc) => {
    if (resolvedDocs.has(doc.id)) return 0;
    const score =
      (doc.amount_gross ? 40 : 0) +
      (doc.document_type === 'contract' ? 30 : 0) +
      (!doc.property_id && !doc.linked_property_id ? 20 : 0) +
      (doc.ai_confidence != null && doc.ai_confidence < 0.7 ? 10 : 0);
    return Math.min(100, score);
  }, [resolvedDocs]);

  const computeEstimatedAmount = React.useCallback((doc) => {
    if (doc.amount_gross) return doc.amount_gross;
    if (doc.contract_data) {
      if (doc.contract_data.monthly_rent) return doc.contract_data.monthly_rent;
      if (doc.contract_data.rent) return doc.contract_data.rent;
    }
    // Tenant-Fallback: suche Mieter aus tenants-Array
    const tenant = tenants.find(t => t.id === doc.linked_tenant_id);
    if (tenant) {
      return tenant.monthly_rent ?? tenant.cold_rent ?? 0;
    }
    return 0;
  }, [tenants]);

  const buildReason = React.useCallback((doc) => {
    const reasons = [];
    if (!doc.property_id && !doc.linked_property_id) reasons.push('kein Objekt zugeordnet');
    if (doc.amount_gross) reasons.push('hoher Betrag');
    if (doc.document_type === 'contract') reasons.push('Vertrag');
    if (doc.ai_confidence != null && doc.ai_confidence < 0.7) reasons.push('niedrige KI-Konfidenz');
    return reasons.length > 0 ? 'Grund: ' + reasons.join(' + ') : null;
  }, []);

  const enrichedDocuments = React.useMemo(() => {
    return sortedDocuments.map(doc => ({
      ...doc,
      _priority: computePriority(doc),
      _estimatedAmount: computeEstimatedAmount(doc),
    }));
  }, [sortedDocuments]);

  const displayDocuments = React.useMemo(() => {
    const sorted = [...enrichedDocuments].sort((a, b) => {
      // Resolved immer ans Ende
      const aResolved = resolvedDocs.has(a.id) ? 1 : 0;
      const bResolved = resolvedDocs.has(b.id) ? 1 : 0;
      if (aResolved !== bResolved) return aResolved - bResolved;
      if (sortBy === 'priority') {
        if (b._priority !== a._priority) return b._priority - a._priority;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      return 0;
    });
    if (sortBy !== 'priority') {
      // Behalte bestehende Sortierung, aber resolved ans Ende
      return enrichedDocuments.slice().sort((a, b) => {
        const aResolved = resolvedDocs.has(a.id) ? 1 : 0;
        const bResolved = resolvedDocs.has(b.id) ? 1 : 0;
        return aResolved - bResolved;
      });
    }
    return sorted;
  }, [enrichedDocuments, sortBy, resolvedDocs]);

  const claraRecommendations = React.useMemo(() => {
    return [...enrichedDocuments]
      .sort((a, b) => {
        if (b._priority !== a._priority) return b._priority - a._priority;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      })
      .slice(0, 3)
      .filter(doc => doc._priority >= 20);
  }, [enrichedDocuments]);

  const unassignedImpact = React.useMemo(() => {
    return enrichedDocuments
      .filter(d => !d.property_id && !d.linked_property_id)
      .reduce((sum, d) => sum + (parseFloat(d._estimatedAmount) || 0), 0);
  }, [enrichedDocuments]);


  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Debug logging for render

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <div className="relative">
              <FileText className="w-8 h-8 mr-3 text-blue-600" />
              <Sparkles className="w-4 h-4 absolute -top-1 -right-1 text-yellow-500" />
            </div>
            Dokumente
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Intelligente Dokumentenverwaltung für Ihre Immobilien
          </p>
        </div>
        
        {/* Quick Actions */}
        <div className="mt-4 sm:mt-0 flex space-x-2">
          <Button
            variant="outline"
            onClick={() => { setDocumentOffset(0); loadDocuments(0, false); }}
            disabled={loading}
            className="flex items-center"
          >
            <Zap className="h-4 w-4 mr-2" />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-neutral-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700">
          <div className="flex items-center">
            <div className="p-2 bg-gray-700 dark:bg-gray-600 rounded-lg">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Gesamt Dokumente</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalDocuments}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-neutral-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700">
          <div className="flex items-center">
            <div className="p-2 bg-gray-700 dark:bg-gray-600 rounded-lg">
              <Upload className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Speicherplatz</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatFileSize(stats.totalSize)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-neutral-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700">
          <div className="flex items-center">
            <div className="p-2 bg-gray-700 dark:bg-gray-600 rounded-lg">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Diese Woche</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.thisWeek}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-neutral-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700">
          <div className="flex items-center">
            <div className="p-2 bg-gray-700 dark:bg-gray-600 rounded-lg">
              <FolderOpen className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Kategorien</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.categories || categories.length || 0}</p>
            </div>
          </div>
        </div>
      </div>


      {/* ✨ Clara Insight Banner */}
      {documents.length > 0 && (() => {
        const unassigned = documents.filter(d => !d.property_id && !d.tenant_id).length;
        const contracts = documents.filter(d => d.document_type === 'contract').length;
        const invoices = documents.filter(d => d.category === 'Rechnung' || d.invoice_number).length;
        const withSummary = documents.filter(d => d.ai_summary).length;
        const insights = [];
        if (unassigned > 0) insights.push({ icon: '⚠️', text: `${unassigned} Dokument${unassigned > 1 ? 'e' : ''} noch keinem Objekt zugeordnet`, action: 'unassigned', color: 'amber' });
        if (contracts > 0) insights.push({ icon: '📄', text: `${contracts} Vertrag${contracts > 1 ? 'sdokumente' : ''} im Vault`, action: null, color: 'blue' });
        if (invoices > 0) insights.push({ icon: '🧾', text: `${invoices} Rechnung${invoices > 1 ? 'en' : ''} erkannt`, action: null, color: 'green' });
        if (withSummary > 0) insights.push({ icon: '🧠', text: `Clara hat ${withSummary} Dokument${withSummary > 1 ? 'e' : ''} analysiert`, action: null, color: 'purple' });
        if (insights.length === 0) return null;
        return (
          <div className="mb-6 bg-gray-900 border border-gray-700 rounded-xl p-5" style={{background: 'var(--clara-banner-bg, #111827)', borderColor: 'var(--clara-banner-border, #374151)'}}>
            <style>{`
              :root { --clara-banner-bg: #111827; --clara-banner-border: #374151; --clara-banner-text: #e5e7eb; --clara-banner-sub: #9ca3af; }
              .light :root { --clara-banner-bg: #eef2ff; --clara-banner-border: #c7d2fe; --clara-banner-text: #1e1b4b; --clara-banner-sub: #4338ca; }
              .light { --clara-banner-bg: #eef2ff; --clara-banner-border: #c7d2fe; --clara-banner-text: #1e1b4b; --clara-banner-sub: #4338ca; }

            `}</style>
            <div className="flex items-start gap-4">
              <div className="p-2 bg-indigo-600 rounded-lg flex-shrink-0 mt-0.5">
                <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.347.347a3.5 3.5 0 01-4.95 0l-.347-.347z" /></svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold mb-3" style={{color: 'var(--clara-banner-text, #e5e7eb)'}}>
                  Clara · {insights.length} relevante Erkenntnisse
                </p>
                <div className="space-y-2">
                  {insights.map((insight, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          insight.color === 'amber' ? 'bg-amber-400' :
                          insight.color === 'blue' ? 'bg-blue-400' :
                          insight.color === 'green' ? 'bg-emerald-400' :
                          'bg-violet-400'
                        }`}></span>
                        <span className="text-sm" style={{color: 'var(--clara-banner-sub, #9ca3af)'}}>{insight.text}</span>
                      </div>
                      {insight.action === 'unassigned' && (
                        <button
                          onClick={() => setSelectedStatus('unassigned')}
                          className="text-xs font-medium px-2 py-0.5 rounded transition-colors ml-3 flex-shrink-0 border"
                          style={{color: 'var(--clara-banner-sub, #818cf8)', borderColor: 'var(--clara-banner-border, #4338ca)'}}
                        >
                          Jetzt prüfen →
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Clara Feed - Priorisierte Entscheidungsfläche */}
      {(() => {
        const unassigned = documents.filter(d => !d.property_id && !d.linked_property_id).length;
        const contracts = documents.filter(d => d.document_type === 'contract' || d.category === 'contract').length;
        const invoices = documents.filter(d => d.document_type === 'invoice' || d.category === 'invoice').length;
        const withSummary = documents.filter(d => d.ai_summary).length;
        const needsReview = documents.filter(d => d.confidence_score && d.confidence_score < 0.7).length;

        const feedEvents = [];

        // HIGH priority: action required
        if (unassigned > 0) feedEvents.push({
          type: 'action',
          priority: 'high',
          title: 'Zuordnung erforderlich',
          message: `${unassigned} Dokument${unassigned > 1 ? 'e sind' : ' ist'} keinem Objekt zugeordnet`,
          reason: unassignedImpact > 0 ? 'Geschätzter Betrag: ' + unassignedImpact.toLocaleString('de-DE', {minimumFractionDigits: 0, maximumFractionDigits: 0}) + ' € · Kann zu falscher Kostenzuordnung führen' : 'Kann zu falscher Kostenzuordnung und steuerlichen Problemen führen',
          action: 'Jetzt prüfen',
          filter: 'unassigned',
          icon: '⚠️'
        });

        if (needsReview > 0) feedEvents.push({
          type: 'action',
          priority: 'high',
          title: 'Prüfung empfohlen',
          message: `${needsReview} Dokument${needsReview > 1 ? 'e haben' : ' hat'} niedrige KI-Konfidenz`,
          action: 'Prüfen',
          filter: 'needs_review',
          icon: '🔍'
        });

        // MEDIUM priority: insights
        if (contracts > 0) feedEvents.push({
          type: 'insight',
          priority: 'medium',
          title: 'Verträge erkannt',
          message: `${contracts} Vertragsdokument${contracts > 1 ? 'e' : ''} im Vault`,
          reason: 'Laufzeiten und Kündigungsfristen prüfen empfohlen',
          action: null,
          icon: '📄'
        });

        if (invoices > 0) feedEvents.push({
          type: 'insight',
          priority: 'medium',
          title: 'Rechnungen erkannt',
          message: `${invoices} Rechnung${invoices > 1 ? 'en' : ''} wurden erkannt`,
          reason: 'Steuerlich relevant – Belegpflicht beachten',
          action: null,
          icon: '🧾'
        });

        // LOW priority: info
        if (withSummary > 0) feedEvents.push({
          type: 'info',
          priority: 'low',
          title: null,
          message: `Clara hat ${withSummary} Dokument${withSummary > 1 ? 'e' : ''} analysiert`,
          action: null,
          icon: '🧠'
        });

        // Clara Empfehlungen (Top 3 nach Priority-Score) — mit Trust Layer
        if (claraRecommendations.length > 0) {
          const typeMap = {contract:'Vertrag',insurance_policy:'Versicherung',general:'Allgemein',invoice:'Rechnung',report:'Bericht'};
          const recTexts = claraRecommendations.map(doc => {
            const typLabel = typeMap[(doc.document_type || '').toLowerCase()] || doc.document_type || 'Dokument';
            const amtStr = doc._estimatedAmount > 0 ? ' · ' + parseFloat(doc._estimatedAmount).toLocaleString('de-DE', {minimumFractionDigits: 0, maximumFractionDigits: 0}) + ' €' : '';
            const action = (!doc.property_id && !doc.linked_property_id) ? 'Zuordnen' : (doc.ai_confidence < 0.7 ? 'Prüfen' : 'Öffnen');
            const name = doc.name ? doc.name.substring(0, 28) : typLabel;
            const reason = buildReason(doc);
            return name + amtStr + ' → ' + action + (reason ? ' (' + reason.replace('Grund: ', '') + ')' : '');
          });
          feedEvents.push({
            type: 'recommendation',
            priority: 'medium',
            title: 'Clara empfiehlt',
            message: recTexts.join(' · '),
            reason: null,
            action: 'Nach Priorität sortieren',
            filter: 'priority_sort',
            icon: '🧠'
          });
        }
        // Erledigt-Counter im Feed
        const resolvedCount = enrichedDocuments.filter(d => resolvedDocs.has(d.id)).length;
        if (resolvedCount > 0) {
          feedEvents.push({
            type: 'progress',
            priority: 'low',
            title: 'Fortschritt heute',
            message: `${resolvedCount} Dokument${resolvedCount > 1 ? 'e' : ''} als erledigt markiert`,
            action: null,
            icon: '✓'
          });
        }

        // Sort by priority
        const order = { high: 0, medium: 1, low: 2 };
        const sorted = feedEvents.sort((a, b) => order[a.priority] - order[b.priority]);
        const visible = sorted.slice(0, 2);

        if (visible.length === 0) return null;

        const now = new Date();
        const timeStr = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
        return (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-xs font-medium text-gray-500 dark:text-neutral-500 uppercase tracking-wide">Clara Empfehlungen</span>
              <span className="text-xs text-gray-400 dark:text-neutral-600">Aktualisiert {timeStr}</span>
            </div>
            <div className="space-y-2">
            {visible.map((event, i) => (
              <div
                key={i}
                className={`flex justify-between items-center p-4 rounded-lg border transition-all ${
                  event.priority === 'high'
                    ? 'bg-red-50 dark:bg-red-950/60 border-red-200 dark:border-red-800 shadow-sm shadow-red-100 dark:shadow-red-900/20'
                    : event.priority === 'medium'
                    ? 'bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-800'
                    : 'bg-gray-50/50 dark:bg-neutral-900/50 border-gray-100 dark:border-neutral-800/60'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-base leading-none">{event.icon}</span>
                  <div>
                    {event.title && (
                      <p className={`text-sm font-semibold ${
                        event.priority === 'high'
                          ? 'text-red-700 dark:text-red-300'
                          : 'text-gray-900 dark:text-white'
                      }`}>
                        {event.title}
                      </p>
                    )}
                    <p className={`text-sm ${
                      event.priority === 'high'
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-500 dark:text-neutral-400'
                    }`}>
                      {event.message}
                    </p>
                    {event.reason && (
                      <p className="text-xs text-gray-400 dark:text-neutral-500 mt-0.5 italic">
                        → {event.reason}
                      </p>
                    )}
                  </div>
                </div>
                {event.action && (
                  <button
                    onClick={() => {
                      if (event.filter === 'priority_sort') {
                        setSortBy('priority');
                        if (showSuccess) showSuccess('Clara sortiert nach Priorität');
                      } else {
                        setSelectedStatus(event.filter);
                        if (showSuccess) showSuccess('Clara zeigt dir jetzt alle ' + (event.filter === 'unassigned' ? 'unzugeordneten' : 'relevanten') + ' Dokumente');
                      }
                      // Scroll to document list
                      setTimeout(() => {
                        const listEl = document.querySelector('[data-document-list]');
                        if (listEl) listEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                    className={`text-sm font-medium whitespace-nowrap ml-4 px-3 py-1.5 rounded-md border transition-colors ${
                      event.priority === 'high'
                        ? 'text-red-700 dark:text-red-300 border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/50'
                        : 'text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/30'
                    }`}
                  >
                    {event.action} →
                  </button>
                )}
              </div>
            ))}
            </div>
          </div>
        );
      })()}
      {/* Upload Area - Collapsible */}
      <div className="bg-white dark:bg-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-800 mb-6">
        <button
          onClick={() => setUploadExpanded(prev => !prev)}
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 dark:hover:bg-neutral-800/50 transition-colors rounded-xl"
        >
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
              <Upload className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">Dokument hochladen</span>
          </div>
          <svg className={`h-4 w-4 text-gray-400 transition-transform ${uploadExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </button>
        {uploadExpanded && <div className="px-5 pb-5 border-t border-gray-100 dark:border-neutral-800 pt-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="p-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mr-3">
              <Upload className="h-5 w-5 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Dokument hochladen</h3>
          </div>
          {isUploading && (
            <div className="flex items-center space-x-3">
              <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                {uploadProgress.toFixed(0)}% hochgeladen
              </div>
              <div className="w-24 bg-gray-200 dark:bg-slate-800 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Assignment Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              <Building className="h-4 w-4 inline mr-2" />
              Objekt zuordnen
            </label>
            <Select value={uploadProperty} onValueChange={setUploadProperty}>
              <SelectTrigger className="bg-white dark:bg-slate-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white h-12 rounded-lg">
                <SelectValue placeholder="Objekt auswählen" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-900 border-gray-300 dark:border-gray-600">
                <SelectItem value="none" className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">Kein Objekt</SelectItem>
                {properties.map((property) => (
                  <SelectItem key={property.id} value={property.id} className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                    {property.property_name || property.name || `Objekt ${property.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              <User className="h-4 w-4 inline mr-2" />
              Mieter zuordnen
            </label>
            <Select value={uploadTenant} onValueChange={setUploadTenant}>
              <SelectTrigger className="bg-white dark:bg-slate-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white h-12 rounded-lg">
                <SelectValue placeholder="Mieter auswählen" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-900 border-gray-300 dark:border-gray-600">
                <SelectItem value="none" className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">Kein Mieter</SelectItem>
                {tenants.map((tenant) => (
                  <SelectItem key={tenant.id} value={tenant.id} className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                    {tenant.name || `Mieter ${tenant.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Drag and Drop Area */}
        <div
          className={`border-2 border-dashed rounded-xl p-12 text-center transition-all duration-300 ${
            dragActive
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 scale-105"
              : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
          } ${isUploading ? "opacity-50 pointer-events-none" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            multiple
            onChange={handleInputChange}
            className="hidden"
            id="file-upload"
            disabled={isUploading}
          />
          
          <div className="flex flex-col items-center">
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-6"></div>
                <p className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                  Wird hochgeladen...
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Bitte warten Sie, während Ihre Dateien verarbeitet werden
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center mb-6">
                  <div className="p-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full">
                    <Upload className="h-8 w-8 text-white" />
                  </div>
                </div>
                <p className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                  Dateien hier ablegen oder klicken zum Auswählen
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Unterstützt werden PDF, Word, Excel, Bilder und weitere Formate
                </p>
                <label
                  onClick={() => setShowUniversalUpload(true)}
                  className="cursor-pointer inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Dateien auswählen
                </label>
              </>
            )}
          </div>
        </div>
        </div>}
      </div>

      {/* Vault v1: Zuletzt erkannt Banner */}
      {documents.filter(d => d.processing_status === 'needs_review').length > 0 && (
        <div className="mb-4 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-amber-600 dark:text-amber-400 text-sm font-medium">
              ⚡ Clara hat {documents.filter(d => d.processing_status === 'needs_review').length} Dokument{documents.filter(d => d.processing_status === 'needs_review').length !== 1 ? 'e' : ''} analysiert — Zuordnung empfohlen
            </span>
          </div>
          <button
            onClick={() => setSelectedStatus('needs_review')}
            className="text-xs text-amber-700 dark:text-amber-300 underline hover:no-underline"
          >
            Jetzt prüfen →
          </button>
        </div>
      )}
      {/* Search and Filter Controls */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
        {/* Vault v1: Dokument-Typ Filter */}
        {Array.from(new Set(documents.map(d => d.document_type).filter(Boolean))).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="text-xs text-gray-500 dark:text-gray-400 self-center">Typ:</span>
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-2 py-1 text-xs rounded-full border transition-colors ${selectedCategory === "all" ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-indigo-400"}`}
            >
              Alle
            </button>
            {Array.from(new Set(documents.map(d => d.document_type).filter(Boolean))).map(type => {
              const typeLabels = {
                contract: 'Vertrag',
                general: 'Allgemein',
                insurance_policy: 'Versicherung',
                invoice: 'Rechnung',
                lease: 'Mietvertrag',
                receipt: 'Quittung',
                report: 'Bericht',
                permit: 'Genehmigung',
                certificate: 'Zertifikat',
                other: 'Sonstiges',
              };
              const label = typeLabels[type?.toLowerCase()] || type;
              return (
                <button
                  key={type}
                  onClick={() => setSelectedCategory(type)}
                  className={`px-2 py-1 text-xs rounded-full border transition-colors ${selectedCategory === type ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-indigo-400"}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
        {/* Vault v1: Status Filter */}
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="text-xs text-gray-500 dark:text-gray-400 self-center">Status:</span>
          {[
            { value: "all", label: "Alle" },
            { value: "completed", label: "✓ Verknüpft" },
            { value: "needs_review", label: "⚠ Prüfung nötig" },
            { value: "unassigned", label: "○ Unzugeordnet" },
          ].map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setSelectedStatus(value)}
              className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                selectedStatus === value
                  ? value === "completed" ? "bg-green-600 text-white border-green-600"
                    : value === "needs_review" ? "bg-amber-500 text-white border-amber-500"
                    : "bg-gray-600 text-white border-gray-600"
                  : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-indigo-400"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Dokumente durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 bg-white dark:bg-slate-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-lg"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="bg-white dark:bg-slate-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white h-12 rounded-lg">
                <SelectValue placeholder="Kategorie" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-900 border-gray-300 dark:border-gray-600">
                <SelectItem value="all" className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">Alle Kategorien</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category} className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Property Filter */}
          <div>
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="bg-white dark:bg-slate-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white h-12 rounded-lg">
                <SelectValue placeholder="Objekt" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-900 border-gray-300 dark:border-gray-600">
                <SelectItem value="all" className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">Alle Objekte</SelectItem>
                <SelectItem value="none" className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">Ohne Objekt</SelectItem>
                {properties.map((property) => (
                  <SelectItem key={property.id} value={property.id} className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                    {property.property_name || property.name || `Objekt ${property.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sort */}
          <div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="bg-white dark:bg-slate-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white h-12 rounded-lg">
                <SelectValue placeholder="Sortieren" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-900 border-gray-300 dark:border-gray-600">
                <SelectItem value="date" className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                  <Calendar className="h-4 w-4 inline mr-2" />
                  Datum
                </SelectItem>
                <SelectItem value="name" className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                  <FileText className="h-4 w-4 inline mr-2" />
                  Name
                </SelectItem>
                <SelectItem value="size" className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                  <Upload className="h-4 w-4 inline mr-2" />
                  Größe
                </SelectItem>
                <SelectItem value="priority" className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                  <Sparkles className="h-4 w-4 inline mr-2" />
                  Priorität
                </SelectItem>
                <SelectItem value="priority" className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                  <Sparkles className="h-4 w-4 inline mr-2" />
                  Priorität
                </SelectItem>
                <SelectItem value="category" className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                  <Tag className="h-4 w-4 inline mr-2" />
                  Kategorie
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* View Mode */}
          <div className="flex space-x-2">
            <Button
              variant={viewMode === "grid" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("grid")}
              className="flex-1 h-12"
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("list")}
              className="flex-1 h-12"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Documents Display */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <span className="text-lg text-gray-600 dark:text-gray-400">Lade Dokumente...</span>
          </div>
        </div>
      ) : sortedDocuments.length === 0 ? (
        <div className="text-center py-16">
          <div className="p-4 bg-gray-100 dark:bg-slate-900 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center">
            <FileText className="h-12 w-12 text-gray-400" />
          </div>
          <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
            {searchTerm || selectedCategory !== "all" || selectedProperty !== "all" || selectedTenant !== "all" || selectedStatus !== "all"
              ? "Keine Dokumente gefunden"
              : "Lade dein erstes Dokument hoch"}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {searchTerm || selectedCategory !== "all" || selectedProperty !== "all" || selectedTenant !== "all" || selectedStatus !== "all"
              ? "Versuchen Sie andere Suchkriterien oder Filter."
              : null}
          </p>
          {/* Vault v2: Load More Button */}
          {hasMoreDocuments && !loading && !searchTerm && (
            <div className="flex justify-center mt-6 mb-4">
              <button
                onClick={() => loadDocuments(documentOffset + DOCS_PER_PAGE, true)}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <span>Weitere Dokumente laden</span>
                <span className="text-sm opacity-80">({totalDocuments - documents.length} verbleibend)</span>
              </button>
            </div>
          )}
          {/* Vault v2: Document count display */}
          {totalDocuments > 0 && (
            <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-2 mb-4">
              {documents.length} von {totalDocuments} Dokumenten angezeigt
            </div>
          )}
                    {!searchTerm && selectedCategory === "all" && selectedProperty === "all" && selectedTenant === "all" && selectedStatus === "all" && (
            <div className="max-w-sm mx-auto mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-xl text-left">
              <p className="text-sm text-purple-700 dark:text-purple-300 font-medium mb-2">Clara erkennt automatisch:</p>
              <ul className="text-sm text-purple-600 dark:text-purple-400 space-y-1">
                <li>📄 Mietverträge → Mieter &amp; Objekt</li>
                <li>🧾 Rechnungen → Betrag &amp; Kategorie</li>
                <li>🏦 Bankdaten → Zahlungen &amp; Zuordnung</li>
              </ul>
              <p className="text-xs text-purple-500 dark:text-purple-400 mt-2">und ordnet sie deinem Portfolio zu.</p>
            </div>
          )}
          {!searchTerm && selectedCategory === "all" && selectedProperty === "all" && selectedTenant === "all" && (
            <Button
              onClick={() => setShowUniversalUpload(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Erstes Dokument hochladen
            </Button>
          )}
        </div>
      ) : (
        <div data-document-list className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-2"}>
          {displayDocuments.map((document) => (
            <div
              key={document.id}
              className={`bg-white dark:bg-neutral-900 rounded-xl border border-gray-100 dark:border-neutral-800 hover:border-gray-300 dark:hover:border-neutral-600 transition-all duration-150 cursor-pointer group ${
                viewMode === "list" ? "flex items-center px-4 py-3 gap-4" : "p-4 flex flex-col"
              } ${document._priority >= 70 && !resolvedDocs.has(document.id) ? 'border-l-2 border-l-red-400 dark:border-l-red-500' : ''}`}
              onClick={() => handleDocumentClick(document)}
            >
              {viewMode === "grid" ? (
                <>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start flex-1 mr-2 min-w-0">
                      <div className="p-1.5 bg-gray-100 dark:bg-neutral-800 rounded-lg mr-2.5 flex-shrink-0">
                        <FileText className="h-4 w-4 text-gray-500 dark:text-neutral-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 
                          className="text-sm font-medium text-gray-900 dark:text-white leading-snug line-clamp-2"
                          title={document.name}
                        >
                          {document.name}
                        </h3>
                        <p className="text-xs text-gray-400 dark:text-neutral-500 mt-0.5">
                          {formatFileSize(document.file_size)}
                          {document.extracted_fields?.tenant_name && <span className="ml-2">· {document.extracted_fields.tenant_name}</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDocumentClick(document); }}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors"
                        title="Anzeigen"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAnalyzeDocument(document); }}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                        title="Mit Clara analysieren"
                      >
                        <Brain className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteDocument(document.id); }}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title="Löschen"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedDocumentForAssignment(document); setIsAssignmentModalOpen(true); }}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                        title="Zuordnen"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* ✨ Clara Insight Layer v2 - real AI data */}
                  {(() => {
                    const isLinked = !!(document.property_id || document.tenant_id);
                    const hasAmount = document.amount_gross;
                    const hasSupplier = document.supplier_name;
                    const hasSummary = document.ai_summary;
                    
                    // Show AI summary if available
                    if (hasSummary) {
                      return (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 dark:text-neutral-400 line-clamp-2 leading-relaxed">
                            {document.ai_summary.length > 90 ? document.ai_summary.substring(0, 90) + '…' : document.ai_summary}
                          </p>
                          {(hasAmount || hasSupplier) && (
                            <p className="text-xs text-gray-400 dark:text-neutral-500 mt-0.5">
                              {hasSupplier && <span className="mr-2">{document.supplier_name}</span>}
                              {hasAmount && <span className="font-medium text-gray-600 dark:text-neutral-300">{Number(document.amount_gross).toLocaleString('de-DE', {minimumFractionDigits: 2})} €</span>}
                            </p>
                          )}
                        </div>
                      );
                    }
                    // Show invoice data if available (no summary yet)
                    if (hasAmount || hasSupplier) {
                      return (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 dark:text-neutral-400">
                            {hasSupplier && <span className="mr-2">{document.supplier_name}</span>}
                            {hasAmount && <span className="font-medium text-gray-700 dark:text-neutral-200">{Number(document.amount_gross).toLocaleString('de-DE', {minimumFractionDigits: 2})} €</span>}
                          </p>
                        </div>
                      );
                    }
                    // Show unassigned warning
                    if (!isLinked) {
                      return (
                        <div className="mt-2">
                          <div className="flex items-center gap-2">
                          <button
                            className="text-xs text-indigo-500 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                            onClick={(e) => { e.stopPropagation(); setSelectedDocumentForAssignment(document); setIsAssignmentModalOpen(true); }}
                          >
                            Objekt zuordnen →
                          </button>
                          {!resolvedDocs.has(document.id) && (
                            <button
                              className="text-xs text-gray-400 dark:text-neutral-500 hover:text-green-600 dark:hover:text-green-400 transition-colors border border-gray-200 dark:border-neutral-700 px-1.5 py-0.5 rounded"
                              onClick={async (e) => {
                                e.stopPropagation();
                                // Optimistic UI: sofort markieren
                                setResolvedDocs(prev => new Set(prev).add(document.id));
                                try {
                                  await documentsService.resolveDocument(document.id);
                                  showSuccess('Als erledigt markiert – Clara passt Prioritäten an');
                                } catch(err) {
                                  // Rollback bei Fehler: Ghost-State verhindern
                                  setResolvedDocs(prev => {
                                    const copy = new Set(prev);
                                    copy.delete(document.id);
                                    return copy;
                                  });
                                  console.error('Resolve fehlgeschlagen:', err);
                                }
                              }}
                            >
                              ✓ Erledigt
                            </button>
                          )}
                        </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {/* Footer */}
                  <div className="flex items-center justify-between mt-auto pt-2.5 flex-wrap gap-1">
                    {/* Dokument-Typ Badge */}
                    {(document.document_type || document.category) && (
                      <span className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400 rounded">
                        {({'contract':'Vertrag','insurance_policy':'Versicherung','general':'Allgemein','invoice':'Rechnung','report':'Bericht','technical':'Technisch'}[(document.document_type || document.category)?.toLowerCase()] || document.document_type || document.category)}
                      </span>
                    )}
                    {document._priority >= 70 && !resolvedDocs.has(document.id) && (
                      <span className="px-1.5 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded font-medium">Hoch</span>
                    )}
                    {document._priority >= 40 && document._priority < 70 && !resolvedDocs.has(document.id) && (
                      <span className="px-1.5 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded font-medium">Mittel</span>
                    )}
                    {resolvedDocs.has(document.id) && (
                      <span className="px-1.5 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded font-medium">✓ Erledigt</span>
                    )}
                    {/* Vault-Status Badge */}
                    {(document.linked_tenant_id || document.linked_property_id) && (
                      <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>Verknüpft
                      </span>
                    )}
                    {!document.linked_tenant_id && !document.linked_property_id && document.processing_status === 'needs_review' && (
                      <span className="flex items-center gap-1 text-xs text-amber-500 dark:text-amber-400">
                        <span className="w-1.5 h-1.5 bg-amber-400 rounded-full"></span>Prüfen
                      </span>
                    )}
                    {!document.linked_tenant_id && !document.linked_property_id && document.processing_status !== 'needs_review' && (
                      <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-neutral-500">
                        <span className="w-1.5 h-1.5 bg-gray-300 dark:bg-neutral-600 rounded-full"></span>Offen
                      </span>
                    )}
                    {/* Confidence Badge */}
                    {document.confidence != null && document.confidence > 0 && (
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        document.confidence >= 0.9 ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                        : document.confidence >= 0.7 ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"
                        : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
                      }`}>
                        {document.confidence >= 0.9 ? "Hoch" : document.confidence >= 0.7 ? "Gut" : "Prüfen"}
                      </span>
                    )}
                    <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
                      {new Date(document.created_at).toLocaleDateString('de-DE')}
                    </span>
                  </div>

                  {document.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                      {document.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {new Date(document.created_at).toLocaleDateString('de-DE')}
                    </span>
                    {document.property_id && (
                      <span className="flex items-center">
                        <Building className="h-3 w-3 mr-1" />
                        Objekt
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center flex-1 min-w-0">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-4 flex-shrink-0">
                      <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1">
                        {document.name}
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                        <span>{formatFileSize(document.file_size)}</span>
                        {document.category && (
                          <span className="flex items-center">
                            <Tag className="h-3 w-3 mr-1" />
                            {document.category}
                          </span>
                        )}
                        <span className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {new Date(document.created_at).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-0.5 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDocumentClick(document); }}
                      className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors"
                      title="Anzeigen"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAnalyzeDocument(document); }}
                      className="p-2 rounded-lg text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                      title="Mit Clara analysieren"
                    >
                      <Brain className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteDocument(document.id); }}
                      className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      title="Löschen"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Evidence-first viewer: separate from the legacy Supabase document viewer. */}
      {isEvidenceViewerOpen && evidenceArtifactReference && (
        <EvidenceViewerModal
          artifactReference={evidenceArtifactReference}
          isOpen={isEvidenceViewerOpen}
          onClose={() => {
            setIsEvidenceViewerOpen(false);
            setEvidenceArtifactReference(null);
            const next = new URLSearchParams(searchParams);
            next.delete('artifact');
            setSearchParams(next, { replace: true });
          }}
        />
      )}
      {/* Legacy Supabase Document Viewer remains unchanged for existing documents. */}
      {isViewerOpen && selectedDocument && (
        <DocumentViewer
          document={selectedDocument}
          isOpen={isViewerOpen}
          onClose={() => {
            setIsViewerOpen(false);
            setSelectedDocument(null);
          }}
        />
      )}

      {/* Document Analysis Modal */}
     {isAssignmentModalOpen && selectedDocumentForAssignment && (
        <DocumentAssignmentModal
          isOpen={isAssignmentModalOpen}
          onClose={() => setIsAssignmentModalOpen(false)}
          document={selectedDocumentForAssignment}
          onUpdate={() => {
            loadDocuments();
            setIsAssignmentModalOpen(false);
          }}
        />
      )}

      {isAnalysisOpen && analysisDocument && (
        <DocumentAnalysisModal
          document={analysisDocument}
          isOpen={isAnalysisOpen}
          onClose={() => {
            setIsAnalysisOpen(false);
            setAnalysisDocument(null);
          }}
          onAnalysisComplete={handleAnalysisComplete}
        />
      )}

      {/* Universal Document Upload Modal */}      <UniversalDocumentUpload        documentType={uploadDocumentType}        mode="create"        editingCost={null}        isOpen={showUniversalUpload}        onClose={() => setShowUniversalUpload(false)}        onUploadComplete={handleIntelligentUploadComplete}      />

      {/* Contract Review Modal */}
      {showContractReview && (
        <ContractReviewModal
          isOpen={showContractReview}
          onClose={() => {
            setShowContractReview(false);
            setDetectedContractData(null);
            setContractDocument(null);
          }}
          contractData={detectedContractData}
          document={contractDocument}
          onContractCreate={(contract) => {
            console.log('✅ Contract created:', contract);
            showSuccess(`✅ Vertrag "${contract.name}" erfolgreich erstellt!`);
            loadDocuments(); // Reload to show updated document
          }}
        />
      )}

      {/* IntentBridge - Non-invasive contract detection notification */}
      <IntentBridge
        active={intentBridge.active}
        contractData={intentBridge.contractData}
        document={intentBridge.document}
        onAction={handleIntentAction}
      />
    </div>
  );
};

export default DocumentsPage;

// Cache bust
